import React from 'react';
import { formatDate, formatPhoneNumber, stripHTML, convertCurrency } from '../../../utils/helpers';
import { generateGoogleDirection } from '../../../utils/googleMapHelper';
import IconText from '../../1_Atoms/IconText/IconText';
import Link from '../../1_Atoms/Link/Link';
import generalController from '../../../controllers/generalController';
import { languageController } from '../../../controllers/language/languageController';
import ReactHtmlParser from 'react-html-parser';

interface VehicleData {
  title: string;
  model: string;
  modelYear: string;
  description?: string;
  msrp?: number;
  kilometers?: number;
  vin?: string;
}

interface DealerGeolocation {
  distance: number;
  latitude: number;
  longitude: number;
}

interface DealerData {
  name: string;
  address_line_1: string;
  city: string;
  post_code: string;
  website?: string;
  phone_number?: string;
  geolocation: DealerGeolocation;
}

interface UserPosition {
  lat: number;
  lng: number;
}

interface DateAndTimeData {
  date: string;
  time: string;
}

interface CalloutBoxShortProps {
  vehicleData?: VehicleData;
  dealerData?: DealerData;
  userPosition?: UserPosition;
  dateAndTimeData?: DateAndTimeData;
  isOwnVehicleType?: boolean;
  displayPrice?: boolean;
}

const CalloutBoxShort: React.FC<CalloutBoxShortProps> = ({
  vehicleData,
  dealerData,
  userPosition,
  dateAndTimeData,
  isOwnVehicleType = false,
  displayPrice = true
}) => {
  const { name: dealerName, address_line_1, city, post_code, website, phone_number, geolocation } = dealerData || {} as DealerData;

  const language = generalController.detectLanguage();
  const formattedPhoneNumber = phone_number ? formatPhoneNumber(phone_number) : '';

  return (
    <div className={!(window as any).bnp_retailer_mode ? "mz-callout-box mz-callout-box--short" : "mz-callout-box"}>
      {/* Vehicle */}
      {
        vehicleData && !(window as any).bnp_retailer_mode && (
          isOwnVehicleType ? (
            <div className="mz-callout-box__item mz-callout-box__item--vehicle mz-callout-box__item--vehicle-own-type">
              <h5 className="mz-callout-box__car-name">{language === 'en' ? `${vehicleData.modelYear} ${ReactHtmlParser(vehicleData.model)}` : `${ReactHtmlParser(vehicleData.model)} ${vehicleData.modelYear}`}</h5>
              {
                vehicleData.kilometers && (
                  <div className="mz-callout-box__car-odometer">
                    <span className="strong">{languageController.getCopy('global', 'odometer')}:</span> {Number(vehicleData.kilometers).toLocaleString()}
                  </div>
                )
              }
              {
                vehicleData.vin && (
                  <div className="mz-callout-box__car-vin">
                    <span className="strong">{languageController.getCopy('global', 'vin')}:</span> {vehicleData.vin}
                  </div>
                )
              }
            </div>
          ) : (
              <div className="mz-callout-box__item mz-callout-box__item--vehicle">
                <h5 className="mz-callout-box__car-name">{ReactHtmlParser(vehicleData.title)}</h5>
                <p className="m-0">
                  {vehicleData.description ? stripHTML(vehicleData.description) : ''} {displayPrice ? languageController.getCopy('global', 'pricePrefix') : ''} {displayPrice ? vehicleData.msrp ? convertCurrency(vehicleData.msrp, language) : '' : ''}
                </p>
              </div>
            )
        )
      }
      {/* Dealer */}
      {
        dealerData && !(window as any).bnp_retailer_mode && (
          <div className="mz-callout-box__item mz-callout-box__item--dealer">
            <h6 className="mz-callout-box__dealer-name">{dealerName}</h6>
            <p className="font-small ls-normal m-0">{address_line_1}, {city}, {post_code}</p>
            <p className="font-small ls-normal m-0 text--grey">
              {geolocation && geolocation.distance && Number(geolocation.distance.toFixed(0)) > 0 ? geolocation.distance.toFixed(2) + ' km' : ''}
            </p>
            <div className="mz-callout-box__dealer-links">
              {
                website && (
                  <IconText
                    label={(
                      <Link
                        text={languageController.getCopy('global', 'dealerWebsite') || ''}
                        href={website || ''}
                        target="_blank"
                        data-menu-name="Dealer Website"
                        data-form-dealer={dealerName || ''}
                        data-form-event="Visit website"
                      />
                    )}
                    iconType="website"
                    iconSize={1}
                    iconColour="red"
                    iconTop="1px"
                    className="caption"
                  />
                )
              }
              {
                formattedPhoneNumber && (
                  <IconText
                    label={(
                      <Link
                        text={formattedPhoneNumber}
                        href={`tel:${formattedPhoneNumber}`}
                        data-menu-name="Dealer Phone Number"
                        data-form-dealer={dealerName || ''}
                        data-form-event="Dial dealer phone number"
                      />
                    )}
                    iconType="phone"
                    iconSize={1}
                    iconColour="red"
                    iconTop="1px"
                    className="caption"
                  />
                )
              }
              {
                userPosition && (
                  <IconText
                    label={(
                      <Link
                        text={languageController.getCopy('global', 'dealerDirections') || ''}
                        href={generateGoogleDirection(userPosition, { lat: geolocation.latitude, lng: geolocation.longitude }) || ''}
                        target="_blank"
                        data-menu-name="Dealer Directions"
                        data-form-dealer={dealerName || ''}
                        data-form-event="Open google map"
                      />
                    )}
                    iconType="address"
                    iconSize={1}
                    iconColour="red"
                    iconTop="1px"
                    className="caption"
                  />
                )
              }
            </div>
          </div>
        )
      }
      {/* Date and time */}
      {
        dateAndTimeData && (
          <div className="mz-callout-box__item mz-callout-box__item--date-and-time">
            <div className="mz-callout-box__date-item">
              <span className="strong">{languageController.getCopy('global', 'date')}</span> {formatDate(new Date(dateAndTimeData.date), true)}
            </div>
            <div className="mz-callout-box__date-item">
              <span className="strong">{languageController.getCopy('global', 'timePreference')}</span> <span className="mz-callout-box__date-item-time">{languageController.getCopy('global', dateAndTimeData.time)}</span>
            </div>
          </div>
        )
      }
    </div>
  );
};

export default CalloutBoxShort;
